import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { LandingHeader } from '@components/landing/LandingHeader';
import { LandingFooter } from '@components/landing/LandingFooter';

const VIEWER_MODES = ['Gallery', 'Carousel', 'Multi-view', 'Library', 'List', 'Flipbook', 'Reader'];

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography className="font-label" sx={{ fontSize: 11, color: '#8ff5ff', mb: 1.5 }}>
      {children}
    </Typography>
  );
}

// ── Product mockups ────────────────────────────────────────────────────────────

function WindowDots() {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: 'rgba(255,81,250,0.55)' }} />
      <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: 'rgba(143,245,255,0.45)' }} />
      <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.18)' }} />
    </Stack>
  );
}

const CHROME_BAR = {
  px: 2,
  py: 1.25,
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  borderBottom: '1px solid rgba(143,245,255,0.07)',
} as const;

function FileBrowserMockup({ compact = false }: { compact?: boolean }) {
  const items = [
    { name: 'spring-campaign/', folder: true },
    { name: 'hero-wide.jpg', ext: 'JPG', warm: true },
    { name: 'lookbook-v2.jpg', ext: 'JPG', warm: true },
    { name: 'brief-final.pdf', ext: 'PDF', warm: false },
    { name: 'brand-guide.pdf', ext: 'PDF', warm: false },
    { name: 'raw-assets/', folder: true },
  ];
  const shown = compact ? items.slice(0, 4) : items;
  return (
    <Box className="glass-card" sx={{ borderRadius: 3, overflow: 'hidden' }}>
      <Box sx={CHROME_BAR}>
        <WindowDots />
        <Typography sx={{ fontSize: 11, color: 'text.secondary', fontFamily: 'monospace', ml: 0.5 }}>
          / client-handoff
        </Typography>
      </Box>
      <Box sx={{ p: 1 }}>
        {shown.map((item, i) => {
          const accent = item.folder ? '#8ff5ff' : item.warm ? '#ffb347' : '#ff6b6b';
          const bg = item.folder
            ? 'rgba(143,245,255,0.1)'
            : item.warm
            ? 'rgba(255,179,71,0.1)'
            : 'rgba(255,107,107,0.1)';
          const border = item.folder
            ? 'rgba(143,245,255,0.22)'
            : item.warm
            ? 'rgba(255,179,71,0.22)'
            : 'rgba(255,107,107,0.22)';
          return (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 1.5,
                py: 0.85,
                borderRadius: 1.5,
                '&:hover': { bgcolor: 'rgba(143,245,255,0.04)' },
              }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: 1,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: bg,
                  border: `1px solid ${border}`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 8,
                    fontWeight: 800,
                    letterSpacing: '0.04em',
                    color: accent,
                    fontFamily: '"Space Grotesk", monospace',
                  }}
                >
                  {item.folder ? 'DIR' : item.ext}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 13, color: i === 0 ? '#8ff5ff' : 'text.primary', fontWeight: i === 0 ? 600 : 400 }}>
                {item.name}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function GalleryViewerMockup() {
  const seeds = ['gbox-a', 'gbox-b', 'gbox-c', 'gbox-d', 'gbox-e', 'gbox-f'];
  const modes = ['Gallery', 'Library', 'Flipbook'];
  return (
    <Box className="glass-card" sx={{ borderRadius: 3, overflow: 'hidden', p: 0 }}>
      <Box sx={{ ...CHROME_BAR, justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <WindowDots />
          <Typography className="font-label" sx={{ fontSize: 9, color: '#8ff5ff', ml: 0.5, letterSpacing: '0.1em' }}>
            GALLERY
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5}>
          {modes.map((m, i) => (
            <Box
              key={m}
              sx={{
                px: 1.25,
                py: 0.35,
                borderRadius: 1,
                bgcolor: i === 0 ? 'rgba(143,245,255,0.16)' : 'transparent',
                border: i === 0 ? '1px solid rgba(143,245,255,0.32)' : '1px solid transparent',
              }}
            >
              <Typography sx={{ fontSize: 9, color: i === 0 ? '#8ff5ff' : 'text.secondary', fontWeight: i === 0 ? 700 : 400 }}>
                {m}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
      <Box sx={{ p: 1.5, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
        {seeds.map((seed) => (
          <Box
            key={seed}
            component="img"
            src={`https://picsum.photos/seed/${seed}/200/150`}
            alt=""
            sx={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', borderRadius: 1.5, display: 'block' }}
          />
        ))}
      </Box>
    </Box>
  );
}

const DRIVE_URL = 'https://drive.google.com/file/d/1aBcDeFgHiJkLmNoPqRsTu/view?usp=sharing';
const CLEAN_URL = 'yoursite.com/spring-campaign';
const SCRAMBLE_CHARS = '!@#$%ABCDabcd0123456789';

function UrlTransformMockup() {
  const [phase, setPhase] = useState<'idle' | 'scrambling' | 'done'>('idle');
  const [display, setDisplay] = useState(CLEAN_URL);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const frameRef = useRef(0);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const transform = useCallback(() => {
    if (phase === 'scrambling') return;
    if (phase === 'done') {
      setPhase('idle');
      setDisplay(CLEAN_URL);
      return;
    }
    setPhase('scrambling');
    frameRef.current = 0;
    timerRef.current = setInterval(() => {
      const f = ++frameRef.current;
      const revealed = Math.floor((f / 22) * CLEAN_URL.length);
      setDisplay(
        Array.from({ length: CLEAN_URL.length }, (_, i) =>
          i < revealed
            ? CLEAN_URL[i]
            : SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)],
        ).join(''),
      );
      if (f >= 22) {
        clearInterval(timerRef.current!);
        setDisplay(CLEAN_URL);
        setPhase('done');
      }
    }, 55);
  }, [phase]);

  const beforeOpacity = phase === 'done' ? 0.25 : 1;
  const afterOpacity = phase === 'idle' ? 0.3 : 1;

  return (
    <Box className="glass-card" sx={{ borderRadius: 3, p: 2.5 }}>
      <Box
        sx={{
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 2,
          p: 2,
          mb: 1.5,
          transition: 'opacity 0.4s',
          opacity: beforeOpacity,
        }}
      >
        <Typography sx={{ fontSize: 9, color: '#555', letterSpacing: '0.1em', mb: 0.75 }}>
          GOOGLE DRIVE / DROPBOX
        </Typography>
        <Typography
          component="code"
          sx={{ fontSize: 11, color: '#999', wordBreak: 'break-all', fontFamily: 'monospace', lineHeight: 1.6 }}
        >
          {DRIVE_URL}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', my: 1.5 }}>
        <Button
          onClick={transform}
          size="small"
          disabled={phase === 'scrambling'}
          sx={{
            fontSize: 12,
            color: '#8ff5ff',
            border: '1px solid rgba(143,245,255,0.3)',
            borderRadius: 1.5,
            textTransform: 'none',
            px: 2.5,
            bgcolor: 'rgba(143,245,255,0.08)',
            '&:hover': { bgcolor: 'rgba(143,245,255,0.16)' },
            '&:active': { transform: 'scale(0.98)' },
          }}
        >
          {phase === 'done' ? 'Reset' : 'Transform →'}
        </Button>
      </Box>

      <Box
        sx={{
          border: `1px solid ${phase === 'idle' ? 'rgba(143,245,255,0.15)' : 'rgba(143,245,255,0.38)'}`,
          borderRadius: 2,
          p: 2,
          transition: 'opacity 0.5s, border-color 0.4s, background-color 0.4s',
          opacity: afterOpacity,
          bgcolor: phase === 'done' ? 'rgba(143,245,255,0.05)' : 'transparent',
        }}
      >
        <Typography sx={{ fontSize: 9, color: '#8ff5ff', letterSpacing: '0.1em', mb: 0.75 }}>
          GENESISBOX / YOUR DOMAIN
        </Typography>
        <Typography
          component="code"
          sx={{
            fontSize: 13,
            color: '#8ff5ff',
            fontFamily: 'monospace',
            fontWeight: phase === 'done' ? 600 : 400,
            transition: 'font-weight 0.2s',
          }}
        >
          {display}
        </Typography>
      </Box>
    </Box>
  );
}


// ── Comparison matrix ──────────────────────────────────────────────────────────

const COMPARE_FEATURES = [
  'Browse files like Google Drive',
  'Direct, stable link per folder',
  'Embed viewer on any website',
  'Your own domain and storage',
];

const COMPARE_COLS = [
  {
    name: 'Google Drive\nDropbox',
    note: 'Great UI — locked to their platform',
    vals: [true, false, false, false],
    highlight: false,
  },
  {
    name: 'GenesisBox',
    note: 'The layer in between',
    vals: [true, true, true, true],
    highlight: true,
  },
  {
    name: 'Cloudflare R2\nDreamHost · S3',
    note: 'Direct links — no browsable UI',
    vals: [false, true, false, true],
    highlight: false,
  },
] as const;

function Check({ on }: { on: boolean }) {
  return (
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: 1,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: on ? 'rgba(143,245,255,0.14)' : 'rgba(255,255,255,0.04)',
        border: `1px solid ${on ? 'rgba(143,245,255,0.28)' : 'rgba(255,255,255,0.08)'}`,
      }}
    >
      <Typography sx={{ fontSize: 11, fontWeight: 800, color: on ? '#8ff5ff' : 'rgba(255,255,255,0.2)', lineHeight: 1 }}>
        {on ? '+' : '–'}
      </Typography>
    </Box>
  );
}

function ComparisonMatrix() {
  return (
    <Box component="section" sx={{ py: { xs: 5, md: 8 } }}>
      <SectionLabel>Why it exists</SectionLabel>
      <Typography
        sx={{ fontWeight: 800, letterSpacing: '-0.03em', fontSize: { xs: 24, md: 30 }, mb: 2, lineHeight: 1.15, maxWidth: 640 }}
      >
        Google Drive UI. Direct cloud links. Finally both.
      </Typography>
      <Typography sx={{ fontSize: { xs: 15, md: 16 }, color: 'text.secondary', maxWidth: 580, lineHeight: 1.7, mb: 5 }}>
        Drive and Dropbox lock you into their interface with random, opaque URLs. Cloudflare R2 and
        DreamHost give you direct links you control — but no browsable UI. GenesisBox is the layer
        between them.
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1.35fr 1fr' },
          gap: { xs: 2, md: 1.5 },
          alignItems: 'stretch',
        }}
      >
        {COMPARE_COLS.map((col) => (
          <Box
            key={col.name}
            sx={{
              borderRadius: 3,
              p: { xs: 2.5, md: 3 },
              border: '1px solid',
              ...(col.highlight
                ? {
                    borderColor: 'rgba(143,245,255,0.32)',
                    background: 'rgba(143,245,255,0.07)',
                    boxShadow: '0 0 48px rgba(143,245,255,0.06)',
                  }
                : {
                    borderColor: 'rgba(255,255,255,0.07)',
                    background: 'rgba(25,25,28,0.5)',
                  }),
            }}
          >
            {col.highlight && (
              <Chip
                label="GenesisBox"
                size="small"
                sx={{
                  mb: 1.5,
                  bgcolor: 'rgba(143,245,255,0.12)',
                  color: '#8ff5ff',
                  borderRadius: 1,
                  fontWeight: 700,
                  fontSize: 10,
                  letterSpacing: '0.06em',
                  '.MuiChip-label': { px: 1.25 },
                }}
              />
            )}
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: 14, md: 15 },
                color: col.highlight ? '#8ff5ff' : 'text.primary',
                whiteSpace: 'pre-line',
                lineHeight: 1.4,
                mb: 0.75,
              }}
            >
              {col.name}
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 3, lineHeight: 1.5 }}>
              {col.note}
            </Typography>
            <Stack spacing={2}>
              {COMPARE_FEATURES.map((f, i) => (
                <Box key={f} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                  <Check on={col.vals[i]} />
                  <Typography
                    sx={{
                      fontSize: 13,
                      lineHeight: 1.5,
                      color: col.vals[i] ? 'text.primary' : 'text.disabled',
                    }}
                  >
                    {f}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ── Feature section layout ─────────────────────────────────────────────────────

interface FeatureSectionProps {
  id: string;
  label: string;
  title: string;
  body: string;
  imageSlot: React.ReactNode;
  reverse?: boolean;
  children?: React.ReactNode;
}

function FeatureSection({ id, label, title, body, imageSlot, reverse, children }: FeatureSectionProps) {
  return (
    <Box
      component="section"
      id={id}
      sx={{
        py: { xs: 5, md: 8 },
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: { xs: 4, md: 8 },
        alignItems: 'center',
      }}
    >
      <Box sx={{ order: { xs: 2, md: reverse ? 2 : 1 } }}>
        <SectionLabel>{label}</SectionLabel>
        <Typography
          sx={{
            fontWeight: 800,
            letterSpacing: '-0.03em',
            fontSize: { xs: 26, md: 32 },
            maxWidth: 520,
            mb: 2,
            lineHeight: 1.15,
          }}
        >
          {title}
        </Typography>
        <Typography sx={{ fontSize: { xs: 15, md: 16 }, color: 'text.secondary', maxWidth: 460, lineHeight: 1.7 }}>
          {body}
        </Typography>
        {children}
      </Box>
      <Box sx={{ order: { xs: 1, md: reverse ? 1 : 2 } }}>
        {imageSlot}
      </Box>
    </Box>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function LandingPage() {
  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: 'transparent' }}>
      <LandingHeader />

      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 4 } }}>
        {/* Hero */}
        <Box
          component="section"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: 4, md: 7 },
            alignItems: 'center',
            pt: { xs: 7, md: 12 },
            pb: { xs: 5, md: 8 },
          }}
        >
          <Box>
            <Chip
              label="shared_cloud_workspace"
              size="small"
              sx={{
                mb: 2.5,
                bgcolor: 'rgba(143,245,255,0.12)',
                color: '#8ff5ff',
                borderRadius: 1.5,
                '.MuiChip-label': {
                  fontFamily: '"Space Grotesk", sans-serif',
                  fontSize: 10,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                },
              }}
            />
            <Typography
              variant="h1"
              sx={{
                fontWeight: 900,
                letterSpacing: '-0.04em',
                fontSize: { xs: 40, sm: 52, md: 60 },
                lineHeight: 1.04,
                mb: 2.5,
              }}
            >
              One drive for your files.
              <br />
              <Box component="span" className="neon-glow-text" sx={{ color: '#8ff5ff' }}>
                A viewer for everything inside.
              </Box>
            </Typography>
            <Typography
              sx={{ fontSize: { xs: 16, md: 18 }, color: 'text.secondary', maxWidth: 520, lineHeight: 1.7, mb: 4 }}
            >
              Browse your Cloudflare R2 or DreamHost storage like Google Drive. Every folder
              renders as a gallery, library, or PDF reader — with a direct link your clients can
              open anywhere, on your domain.
            </Typography>
            <Button
              component={RouterLink}
              to="/login"
              variant="contained"
              sx={{
                bgcolor: '#8ff5ff',
                color: '#003f43',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 800,
                px: 3.5,
                py: 1.35,
                fontSize: '0.95rem',
                transition: 'transform 200ms cubic-bezier(0.16, 1, 0.3, 1)',
                '&:hover': { bgcolor: '#a6f8ff', transform: 'translateY(-1px)' },
                '&:active': { transform: 'translateY(0)' },
              }}
            >
              Sign in
            </Button>
          </Box>

          <Box
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              border: '1px solid rgba(143,245,255,0.15)',
              boxShadow: '0 0 48px rgba(143,245,255,0.07)',
              aspectRatio: '1/1',
              bgcolor: '#0a0a0d',
              maxWidth: { md: 480 },
              mx: { md: 'auto' },
            }}
          >
            <Box
              component="video"
              src="/hero-video.mp4"
              poster="/hero-poster.png"
              autoPlay
              loop
              muted
              playsInline
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </Box>
        </Box>

        {/* Comparison matrix */}
        <ComparisonMatrix />

        {/* Feature sections */}
        <FeatureSection
          id="features"
          label="One drive, every file"
          title="A shared drive that actually shows your files."
          body="Folders, uploads, drag-and-drop, bulk rename and delete. Each org gets its own storage on Cloudflare R2 or DreamHost, assigned by an admin. Built for teams that hand files to clients."
          imageSlot={<FileBrowserMockup />}
        />

        <FeatureSection
          id="viewer"
          label="View in context"
          title="Built-in gallery, library, and PDF reader."
          body="Open a folder — it renders in the right mode. Photo sets become galleries. Document folders open as paged readers or flipbooks. The viewer reads the content and picks the format."
          imageSlot={<GalleryViewerMockup />}
          reverse
        >
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 3 }}>
            {VIEWER_MODES.map((mode) => (
              <Chip
                key={mode}
                label={mode}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.04)',
                  color: 'text.primary',
                  border: '1px solid rgba(143,245,255,0.14)',
                  borderRadius: 1.5,
                  fontWeight: 500,
                }}
              />
            ))}
          </Stack>
        </FeatureSection>

        <FeatureSection
          id="sharing"
          label="Direct links"
          title="Stable URLs that work anywhere — on your domain."
          body="Unlike Dropbox or Drive, share links in GenesisBox are direct paths to your storage. No random tokens, no third-party interface required. Paste the link in your site, embed the viewer, or send it to a client — it opens cleanly every time."
          imageSlot={<UrlTransformMockup />}
        />

        {/* Closing CTA */}
        <Box
          component="section"
          className="glass-card"
          sx={{
            borderRadius: 3,
            p: { xs: 4, md: 6 },
            my: { xs: 4, md: 6 },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 3,
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 800, letterSpacing: '-0.02em', fontSize: { xs: 22, md: 26 }, mb: 1 }}>
              Sign in to your organization.
            </Typography>
            <Typography sx={{ fontSize: 15, color: 'text.secondary', maxWidth: 520, lineHeight: 1.7 }}>
              Your org is already set up. Sign in with Google or Microsoft — your storage appears once you're approved.
            </Typography>
          </Box>
          <Button
            component={RouterLink}
            to="/login"
            variant="contained"
            sx={{
              bgcolor: '#8ff5ff',
              color: '#003f43',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 800,
              px: 3.5,
              py: 1.4,
              flexShrink: 0,
              fontSize: '0.95rem',
              '&:hover': { bgcolor: '#a6f8ff' },
            }}
          >
            Sign in
          </Button>
        </Box>
      </Box>

      <LandingFooter />
    </Box>
  );
}
